import Close from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import { Dayjs } from "dayjs";
import React, { useMemo, useRef } from "react";
import BoxSpaceBtw, { BoxFlex } from "../../../atoms/boxSpaceBtw";
import FilterSelected from "../../../atoms/my-calender/filter";
import CalenderPicker from "../../../molecules/calender-picker-dialog/calenderPicker";
import { useMyCalenderContext } from "../../../pages/my-calendar/context";
import { defaultFilters } from "../../../pages/my-calendar/helper";
import { isAdmin, isClient, isInternalUser, isSuperAdmin } from "../../../utils/role";
import { LocalDayjs } from "../../../utils/timezoneService";
import { checkObjectValuesSame } from "../../../utils/utils";
import { textStyle } from "../style";
import {
  AccordianExpanded,
  DateType,
  Filters,
  SetDateType,
  SetFilters,
} from "../types";
import ClientFilter from "./client";
import ExpertClientAMPOC from "./expert-client-am-poc";
import { MyCalenderFilterContext } from "./filterContext";
import GroupAndAMFilter from "./groupAndAM";
import { getFormOptions } from "./helper";
import Status from "./status";
import { FormData } from "./type";

type Props = {
  closeFilters: () => void;
  filters: Filters;
  setFilters: SetFilters;
  date: DateType;
  setDate: SetDateType;
  setDateForCalendar?: (d: Dayjs | null) => void; // Client & Expert
};

const FilterPopup = ({
  closeFilters,
  filters,
  setFilters,
  date,
  setDate,
  setDateForCalendar,
}: Props) => {
  // Why this clearFilter useState Used ?
  // so whenever Clear All button is clicked
  // it's state will change and it will help us to reset the forms
  const clearFilter = useRef(0);
  const [expanded, setExpanded] = React.useState<AccordianExpanded>({
    status: false,
    teamAM: false,
    client: false,
    expertClientFilter: false,
  });

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded((prev) => ({
        ...prev,
        [panel]: !!isExpanded,
      }));
    };

  const [formOptions, setFormOptions] = React.useState<FormData>({
    client: [],
    group: [],
    am: [],
    filtered_am: [],
    filtered_client: [],
    client_filter_am: [],
    expert_filter_poc: [],
  });
  const { groupData, filters: c_filters } = useMyCalenderContext();

  const isFiltersDefault = useMemo(() => {
    const currentFilters = { ...c_filters };
    currentFilters.isFilterApplied = false;
    currentFilters.isFilterChange = false;
    currentFilters.arrange_tag = "Day";
    return checkObjectValuesSame(currentFilters, defaultFilters);
  }, [c_filters]);

  React.useEffect(() => {
    if (groupData) {
      getFormOptions(groupData, setFormOptions);
    } else if (!isSuperAdmin()) {
      getFormOptions(groupData, setFormOptions);
    }
  }, [groupData]);

  const is_admin = isAdmin() || isSuperAdmin();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 999,
      }}
    >
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          padding: "1rem",
          borderRadius: "5px",
          marginY: "auto",
          height: "fit-content",
          width: "fit-content",
          overflowY: "auto",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <MyCalenderFilterContext.Provider
          value={{
            expanded,
            setExpanded,
            formOptions,
            setFormOptions,
            clearFilter,
          }}
        >
          {/* Filter Heading */}
          <BoxSpaceBtw>
            <p style={textStyle}>Filters</p>

            <BoxSpaceBtw>
              {!isFiltersDefault && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    clearFilter.current = 1 + clearFilter.current;
                    setFilters((prev) => ({
                      ...defaultFilters,
                      arrange_tag: prev.arrange_tag,
                    }));
                    closeFilters();
                  }}
                >
                  Clear All
                </Button>
              )}
              <IconButton
                onClick={() => {
                  closeFilters();
                }}
              >
                <Close />
              </IconButton>
            </BoxSpaceBtw>
          </BoxSpaceBtw>

          {/* Calendar Filter */}
          <CalenderPicker
            selectDate={date || LocalDayjs()}
            getDate={(date) => {
              setDate(date);
              setDateForCalendar && setDateForCalendar(date);
              closeFilters();
            }}
            dateLabel="Date"
          />

          {/* Call Only Filter */}
          {isInternalUser() && !filters.sidebarFilters.zoom_slot && (
            <BoxFlex sx={{ justifyContent: "flex-end" }}>
              <FormControlLabel
                value="start"
                control={
                  <Switch
                    color="primary"
                    checked={filters.sidebarFilters.call_only}
                    onChange={(e, checked) => {
                      setFilters((prev) => ({
                        ...prev,
                        sidebarFilters: {
                          ...prev.sidebarFilters,
                          call_only: checked,
                        },
                      }));
                    }}
                  />
                }
                label="Call Only"
                labelPlacement="start"
              />
            </BoxFlex>
          )}
          {/* Zoom Call Filter */}
          {is_admin && !filters.sidebarFilters.zoom_slot && (
            <BoxFlex sx={{ justifyContent: "flex-end" }}>
              <FormControlLabel
                value="start"
                control={
                  <Switch
                    color="primary"
                    checked={filters.sidebarFilters.zoom_call}
                    onChange={(e, checked) => {
                      setFilters((prev) => ({
                        ...prev,
                        sidebarFilters: {
                          ...prev.sidebarFilters,
                          zoom_call: checked,
                        },
                      }));
                    }}
                  />
                }
                label="My Zoom Calls"
                labelPlacement="start"
              />
            </BoxFlex>
          )}
          {/* Zoom Slots*/}
          {is_admin && (
            <BoxFlex sx={{ justifyContent: "flex-end" }}>
              <FormControlLabel
                value="start"
                control={
                  <Switch
                    color="primary"
                    checked={filters.sidebarFilters.zoom_slot}
                    onChange={(e, checked) => {
                      setFilters((prev) => ({
                        ...prev,
                        sidebarFilters: {
                          ...prev.sidebarFilters,
                          zoom_slot: checked,
                        },
                      }));
                      closeFilters();
                    }}
                  />
                }
                label="Zoom Slots"
                labelPlacement="start"
              />
            </BoxFlex>
          )}

          {/* Status Filter */}
          {!filters.sidebarFilters.zoom_slot && (
            <Accordion
              sx={{ mt: "1rem" }}
              expanded={expanded.status}
              onChange={handleChange("status")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <FilterSelected
                  label="Status"
                  isSelected={!!filters.sidebarFilters.status}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Status />
              </AccordionDetails>
            </Accordion>
          )}

          {is_admin ? (
            <>
              {/*  GROUP & AM Filter */}
              {!filters.sidebarFilters.zoom_slot && (
                <Accordion
                  expanded={expanded.teamAM}
                  onChange={handleChange("teamAM")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <FilterSelected
                      label="Select Team & AM"
                      isSelected={
                        !!filters.sidebarFilters.am ||
                        !!filters.sidebarFilters.group
                      }
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <GroupAndAMFilter />
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Client Filter */}
              {!filters.sidebarFilters.zoom_slot && (
                <Accordion
                  expanded={expanded.client}
                  onChange={handleChange("client")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <FilterSelected
                      label="Select Client Details"
                      isSelected={!!filters.sidebarFilters?.client_contacts || !!filters.sidebarFilters?.fk_client || !!filters.sidebarFilters?.client_type}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <ClientFilter />
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          ) : (
            isClient() && (
              <>
                {/* Client Filter */}
                <Accordion
                  expanded={expanded.expertClientFilter}
                  onChange={handleChange("expertClientFilter")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <FilterSelected
                      label={
                        isClient() ? "Select Account Manager" : "Select POCs"
                      }
                      isSelected={
                        isClient()
                          ? !!filters.sidebarFilters.client_am
                          : !!filters.sidebarFilters.expert_poc
                      }
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <ExpertClientAMPOC />
                  </AccordionDetails>
                </Accordion>
              </>
            )
          )}
        </MyCalenderFilterContext.Provider>
      </Box>
    </Box>
  );
};

export default FilterPopup;
